<template>
    <div class="filter-section">
        <div class="title" @click="showItems = !showItems">
            <ArrowIcon v-if="isMobile && showItems" position="left" />
            <span>
                {{$t(title)}}
            </span>
            <ArrowIcon v-if="!isMobile || !showItems" :reverse="showItems" />
        </div>
        <ul :class="['filter-options', {hide: !showItems && !hasActiveItems}]">
            <template v-if="transformedItems && transformedItems.length > 0">
                <li v-for="(item, index) in transformedItems" :key="`${item.id}_${index}`" :class="{disabled: isDisabled(item)}" class="fila">
                    <input type="checkbox" :id="`item_input_${item.slug}_${index}`" @change="toggle(item)"
                        :checked="isActive(item)" :disabled="isDisabled(item)" class="raiz">
                    <label :for="`item_input_${item.slug}_${index}`">
                        <span class="input-with-children" v-if="item.children?.length > 0"
                            @click.prevent="toggleChildren(item.id)">
                            <span>{{item.name}}</span>
                            <ArrowIcon :reverse="showChildren(item.id)" />
                        </span>
                        <span v-else>{{item.name}}</span>
                    </label>
                    <template v-if="item.children?.length > 0">
                        <ul class="filter-options filter-children grupo" :class="{hide: !showChildren(item.id)}">
                            <li v-for="(child, index) in item.children" :key="`${child.id}_${index}`">
                                <input type="checkbox" :id="`item_input_${item.slug}_${child.slug}_${index}`" @change="toggle(child, item)"
                                    :checked="isActive(child)">
                                <label :for="`item_input_${item.slug}_${child.slug}_${index}`">
                                    <span class="input-with-children" v-if="child.children.length > 0"
                                        @click.prevent="toggleChildren(child.id)">
                                        <span>{{child.name}}</span>
                                        <ArrowIcon :reverse="showChildren(child.id)" />
                                    </span>
                                    <span v-else>{{child.name}}</span>
                                </label>
                                <template v-if="child.children.length > 0">
                                    <ul class="filter-options filter-children grupo" :class="{hide: !showChildren(child.id)}">
                                        <li v-for="(subChild, index) in child.children" :key="`${subChild.id}_${index}`">
                                            <input type="checkbox" :id="`item_input_${item.slug}_${subChild.slug}_${index}`"
                                            @change="toggle(subChild, child, item)" :checked="isActive(subChild)"> 
                                            <label :for="`item_input_${item.slug}_${subChild.slug}_${index}`">
                                                {{subChild.name}}
                                            </label>
                                        </li>
                                    </ul>
                                </template>
                            </li>
                        </ul>
                    </template>
                </li>
            </template>
            <FilterSkeleton v-else :repeat="expectedItems" />
        </ul>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import filters from '@/helpers/filters'
import FilterSkeleton from './FilterSkeleton.vue';
import ArrowIcon from '@/components/icons/ArrowIcon.vue';
import { useScreen } from 'vue-screen';
import { useUserStore } from '@/store/user';
import { storeToRefs } from 'pinia';


const props = defineProps({
    items: {
        type: Array,
        required: true
    },
    title: {
        type: String,
        required: true
    },
    type: {
        type: String,
        required: true
    },
    expectedItems: {
        type: Number,
        required: true
    }
})



const router = useRouter()
const route = useRoute()
const screen = useScreen()
const userStore = useUserStore()
const { isPress } = storeToRefs(userStore)
let showItems = ref(false)
const activeItems = computed(() => route.params[props.type])
const isActive = (item) => activeItems?.value ? activeItems.value.find(activeItem => activeItem === item.slug) : false
const allowedParams = ['categories', 'fileTypes', 'marketingMaterials', 'bySpaces']
const nodeChildren = ref([])
const itemsLength = computed(() => (props.items.length > 0) ? props.items.length : props.expectedItems)
const itemsChildrenLength = computed(() => 
    props.items.reduce((total, item) => total + (item.children?.length || 0), 0)
);
const itemsLengthFinal = computed(() => itemsLength.value + itemsChildrenLength.value)
const isMobile = computed(() => screen.width <= 687)

const disabledTypes = ['2D Files', '3D / REVIT / BIM files'];
const isDisabled = computed(() => (item) => props.type === 'fileTypes' && disabledTypes.includes(item.name) && isPress.value)

const hasActiveItems = computed(() => {
    return transformedItems.value.some(item => isActive(item) || item.children?.some(child => isActive(child)));
})

function transformSubChildren(items) {
    return items.map(item => {
        const normalizedChildren = item.children
            ? item.children.map(child => {
                const childChildren = child.children && !Array.isArray(child.children)
                    ? Object.values(child.children)
                    : child.children;

                return {
                    ...child,
                    order: Number(child.order) || null,
                    children: childChildren ? transformSubChildren(childChildren) : childChildren,
                };
            }).sort((a, b) => (a.order === null) - (b.order === null) || a.order - b.order)
            : item.children;

        return {
            ...item,
            order: Number(item.order) || null,
            children: normalizedChildren,
        };
    }).sort((a, b) => (a.order === null) - (b.order === null) || a.order - b.order);
}

const transformedItems = computed(() => {
    let items = transformSubChildren(props.items);
    if (props.type === 'bySpaces') {
        items = items.sort((a, b) => (a.acf.order === null) - (b.acf.order === null) || a.acf.order - b.acf.order);
    }
    
    return items;
});

function showChildren(id) {
    return Boolean(nodeChildren.value[id])
}
function toggle(item, father = null, grandFather = null) {
    const params = structuredClone(route.params);
    const slug = item.slug;
    const type = props.type;

    params[type] = params[type] || [];
    const index = params[type].indexOf(slug);

    if (index > -1) {
        params[type] = deactivate(item, params);
        if (father !== null) {
            params[type] = deactivate(father, params, true);
        }
        if (grandFather !== null) {
            params[type] = deactivate(grandFather, params, true);
        }
    } else {
        params[type] = activate(item, params);
    }

    const isModels = route.path == '/' || route.path.startsWith('/models');
    computeRoute(isModels, params);
}

const toggleChildren = (id) => {
    const item = findItemById(id, transformedItems.value);
    if (item) {
        toggleChildrenRecursively(item, !showChildren(id));
    }
};


function computeRoute(isModels, params) {
    let path = ''
    const arr = ['gallery-images', 'galeria', 'white-image', 'blanco', 'environmental-image', 'entorno']
    if( isModels && params['fileTypes'] && params['fileTypes'].length > 0 && arr.includes(params['fileTypes'][0])) {
        path = '/models/file-types/' + `${params['fileTypes'].join('/')}`
    } else {
        const sortedFilters = ['categories', 'fileTypes', 'marketingMaterials', 'bySpaces']
        const keys = Object.keys(params).sort((a, b) => sortedFilters.indexOf(a) - sortedFilters.indexOf(b))
        for (let param of keys) {
            /*  
                Necesitamos filtrar por parámetros permitidos porque de lo contrario incluiría parámetros de
                otras rutas en esta y el sistema buscaría filtros que satisfagan esos parámetros.
            */
            if (params[param].length > 0 && allowedParams.includes(param))
                /*
                    Ordenamos los parametros para evitar crear urls duplicadas con diferente orden.
                    
                    Convertimos el nombre del parámetro a kebab-case, porque por convenio las urls
                    están especificadas en kebab-case pero los parámetros en camelCase. Dado que usamos
                    el nombre del parámetro como prefijo para cada sección, necesitamos parsear el nombre
                    del parámetro.
                */
                path += `/${filters.camelToKebabCase(param)}/${params[param].join('/')}`
        }
    }
    if (path === '') path = '/'
    /*  
        Usamos replace en lugar de push porque no nos interesa que se añada al historial de navegación 
        cada vez que se actualiza un filtro.
    */
    router.replace({ path })
}

function activate(item, params) {
    const slug = item.slug;
    const type = props.type;
    params[type] = params[type] || [];
    
    if (!params[type].includes(slug)) {
        params[type].push(slug);
    }

    if (item.children) {
        Object.values(item.children).forEach(child => {
            params[type] = activate(child, params);
        });
    }

    return params[type];
}

function deactivate(item, params, onlyFather = false) {
    const slug = item.slug;
    const type = props.type;
    const index = params[type]?.indexOf(slug);

    if (index > -1) {
        params[type].splice(index, 1);
    }

    if (!onlyFather && item.children) {
        Object.values(item.children).forEach(child => {
            params[type] = deactivate(child, params);
        });
    }

    return params[type];
}

function toggleChildrenRecursively(item, expand) {
    nodeChildren.value[item.id] = expand;

    if (item.children?.length > 0) {
        item.children.forEach(child => {
            toggleChildrenRecursively(child, expand);
        });
    }
}

function findItemById(id, items) {
    for (const item of items) {
        if (item.id === id) return item;
        if (item.children?.length > 0) {
            const found = findItemById(id, item.children);
            if (found) return found;
        }
    }
    return null;
}

const emit = defineEmits(['toggle'])
watch(() => showItems.value, () => {
    emit('toggle', showItems.value)
})

</script>


<style scoped lang="scss">
.input-with-children {
    display: inline-flex;
    column-gap: 0.4rem;
    align-items: center;
}

.filter-section {
    border-top: 0.5px solid rgba(96, 96, 96, 0.7);
    @include tracking(50);

    font-family: 'Tw Cen MT';
    font-weight: 300;
    font-size: 0.875rem;
    line-height: 1.75rem;
    height: auto;
    color: $mid-grey-color;
    transition: 0.3s;


    &>ul {
        // label span{
        //     text-transform: lowercase;
        //     &::first-letter, &.input-with-children span::first-letter{
        //         text-transform: capitalize;
        //     }
        // }
        padding-bottom: 20px;
        list-style: none;
    }

    &:hover {
        background: $corporate-light-color;
        cursor: pointer;
    }

    &.expanded {
        background: $corporate-light-color;
    }

    .filter-options {
        transition: 0.4s;

        @include for-phone-only() {
            transition: 0s;
        }

        transition-timing-function: linear;
        max-height: calc(v-bind(itemsLengthFinal) * 50px); // You must move this param to adjust transition time effect.
        opacity: 1;
        margin: 0;

        &.hide {
            max-height: 0;
            opacity: 0;
            padding-bottom: 0;
            pointer-events: none; //This prevent unintended clicks on checkboxes    
        }
    }

    &>.title {
        text-transform: uppercase;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        padding: 20px;
        gap: 0.4rem;

        svg {
            width: 0.75rem;
        }

        @include for-phone-only() {
            align-items: flex-start;

            svg {
                width: 0.5rem;

                &:last-child {
                    padding-top: 10px;
                }

                &:first-child {
                    padding-left: 20px;
                }
            }
        }

    }

    &:last-child {
        border-bottom: 0.5px solid rgba(96, 96, 96, 0.7);
    }

    .icon-arrow {
        width: 0.75rem;
        font-size: 0.5rem;
        color: #968A7E;
        opacity: 70%;
        transition: 0.5s;

        &::before {
            display: flex;
            align-items: center;
            height: 100%;
        }

        &.open {
            transform: rotate(180deg);
        }
    }
}

ul {
    text-decoration: none;
    list-style: none;

    &.filter-options {
        padding: 0 20px 20px;

        @include for-phone-only() {
            padding: 0 39px 20px;
        }

        li.disabled {
            color: #969696;

            label {
                cursor: default;

                &::before {
                    border: 0.063rem solid #969696;
                }
            }
        }
    }

    &.filter-children {
        padding: 0;
        padding-left: 20px;
    }
}

@include for-phone-only() {
    .filter-section {
        border: none;

        &:last-child {
            border-bottom: none;
        }

        .title {
            padding: .3rem;
            justify-content: flex-start;
            gap: .3rem;

            .icon-arrow {
                font-size: .4rem;
            }
        }
    }
}

.fila {
    line-height: 15px;
    margin-bottom: 10px !important;
}
.grupo {
    line-height: 25px;
}
.grupo li:first-child {
    padding-top: 8px;
}

input.raiz[type=checkbox]+label::before {
    align-self: flex-start;
}
</style>